import { ELanguage } from '@/common'
import { IPaginator, TResponse } from '../model'

export type TSkill = {
    isExisted: boolean
    skillId: number
    skillWeight: number
    skillName: string
}
export type TBenefit = {
    benefitId: number
    benefitIconName: string
    benefitName: string
    benefitNameVI: string
    benefitValue: string
}
export type TWorkingLocation = {
    workingLocationId: number
    addressId: number
    cityId: number
    districtId: number
    address: string
    geoLoc: {
        lat: number
        lon: number
    }
    cityName: string
    cityNameVI: string
}
type TIndustry = {
    industryId: number
    industryName: string
    industryNameVI: string
}
export type TIndustryV3 = {
    industryV3Id: number
    industryV3Name: string
    industryV3NameVI: string
}
export type TJobFunction = {
    parentId: number
    parentName: string
    parentNameVI: string
    children: {
        id: number
        name: string
        nameVI: string
    }[]
}

export type TCompanyInfo = {
    companyId: number
    bannerDesktopUri: string
    jobImageURLs: string[]
    jobVideoURLs: string[]
    companyName: string
    companyLogoURL: string
    companySizeId: string
    companyProfile: string
    contactName: string
    contactEmail: string
    website: string
    address: string
    allowRedirect: number
    whyWorkWithUs: string
    profileBaseName: string
    isUpgradeProfile: number
    isDeleteProfile: number
    isAllowUserEdit: number
    companyProfileBannerUrl: string
    companyProfileUrl: string
    allowRedirectJobIds: string
}

export interface IExtraCompanyInfo extends TCompanyInfo {
    companyLogo: string
    companyUrl: string
    companyCCPUrl: string
    companySize: string
}

export type TJob = {
    jobId: number
    jobTitle: string
    userId: number
    companyLogo: string
    alias: string
    createdOn: string
    approvedOn: string
    expiredOn: string
    lastUpdatedOn: string
    lastSyncedOn: string
    companyName: string
    companyId: number
    companyProfile: string
    companySiddzeId: number
    contactName: string
    createdBy: number
    isOnline: boolean
    isActive: boolean
    isApproved: boolean
    isUnpaidDisable: number
    isCompleted: boolean
    isAnonymous: boolean
    isSalaryVisible: boolean
    isShowLogo: boolean
    isShowLogoInSearch: boolean
    jobDescription: string
    jobRequirement: string
    jobLevelId: number
    languageId: number
    languageSelectedId: number
    languageSelectedVI: string
    languageSelected: string
    redirectTo: string
    requiredCoverLetter: number
    salary: string
    salaryMax: number
    salaryMin: number
    typeWorkingId: number
    skills: Partial<TSkill>[]
    benefits: Partial<TBenefit>[]
    workingLocations: Partial<TWorkingLocation>[]
    address: string
    industries: Partial<TIndustry>[]
    industriesV3: Partial<TIndustryV3>[]
    jobLevel: string
    jobLevelVI: string
    jobLevelText?: string
    locations?: any[]
    canonical: string
    isShowContact: boolean
    salaryRangeId: number
    requiredResume: boolean
    companySize: string
    companySizeVI: string

    simpleServices: string[]
    services: {
        serviceCode: string
        startOn: string
        expiredOn: string
    }[]
    prettySalary: string
    durationDays: number
    numOfApplications: number
    emailAddress: string
    isMobileHotJob: boolean
    isMobileTopJob: boolean
    isBoldAndRed: boolean
    isBoldAndRedM: boolean
    isUrgentJob: boolean
    isUrgentJobM: boolean
    onlineOn: string
    visibilityDisplay: boolean
    priorityOrder: string
    nearestGeoLoc: any
    profilePublishedSiteMask: number
    jobFunction: Partial<TJobFunction>
    yearsOfExperience: number
    highestDegreeId: number
    ageMin: number
    ageMax: number
    isShowAge: number
    nationalityId: number
    isShowNationality: number
    genderId: number
    isShowGender: number
    maritalStatusId: number
    isShowMaritalStatus: number
    numberOfRecruits: number
    isShowNumberOfRecruits: number
    rangeAge: string
    workingDays: string
    workingFromHour: string
    workingToHour: string
    onlineServiceCode?: string
}

export interface IJobInfo extends Partial<TJob> {
    logo: string
    title: string
    link: string
    locationsVI: string
    isTopLevelJob: boolean // this will be calculated
    isApplied: boolean // this will be retrieved from another API
    isSaved: boolean // this will be retrieved from another API
    isMatching: boolean // this will be retrieved from another API
    isBlackList: boolean
    isViewed: boolean // this will be retrieved from another API
}

export type TJobInvitation = {
    jobInvitationId: number
    status: EJobInvitationStatus
    content: string
}

export type TSpecialJob = {
    isSavedJob: boolean
    isApplied: boolean
    isUserBlacklist: boolean
}

export interface IResponseSearchJobs
    extends TResponse<Partial<IJobInfo>[], IPaginator> {
    facets: {
        companies?: any
        cityIds: number[] | any
    }
}
export interface IJobDetail extends TJob {
    notAppliedJob: boolean
    checkBlacklist: boolean
    isSavedJob: boolean
    isTopPriority: boolean
    isTopPriorityM: boolean
    companyInfo: Partial<TCompanyInfo>
    isPremiumJob: boolean
    isHeadHuntJob: boolean
    isTopManagementJob: boolean
    isAdrLiteJob: boolean
    numOfViews: number
    isBanned: boolean
    isExpired: boolean
    jobQuantity: number
    companyColor: string
    jobInvitation: TJobInvitation
}

type TJobSpecialInfoAttributes = {
    applied: boolean
    matching: boolean
    redirectTo: string
    saved: boolean
    userBlackList: boolean
    viewed: boolean
    jobInvitation: TJobInvitation
}

export type TJobSpecialInfo = {
    attributes: TJobSpecialInfoAttributes
    id: number
    type: 'job'
}

export enum ESaveJob {
    save = 'save',
    unsave = 'unsave'
}

export enum EJobInvitationStatus {
    WAITING = 0,
    ACCEPTED = 1,
    REJECTED = 2
}

export type TWinRate = {
    code: number
    result: {
        score: number
    }
}

export type TReport = {
    reportId: number
    reasonIds: number[]
    jobId: number
    userId: number
    status: number
    description: string
    createdOn: string
    userName: string
    reasonEn: string
    jobUrl: string
}

export type TMatchingJob = {
    jobId: number
    userId: number
    matchSkills: {
        jobSkills: Partial<TSkill>[]
        userSkills: Partial<TSkill>[]
        matchedSkills: Partial<TSkill>[]
        notMatchedSkills: Partial<TSkill>[]
        countMatching: number
        skillsTotal: number
        percentage: number
    }
    matchWorkingExperience: {
        jobLevel: number
        userJobLevel: number
        countMatching: number
        isMatch: boolean
        levelTotal: number
        percentage: number
    }
    matchCareerPath: {
        matchJobTitle: {
            jobTitle: string
            isMatch: boolean
        }
        matchCompanyIndustry: {
            industry: {
                entryId: number
                resumeId: number
                industryV3Id: number
                industryV3Name: string
                updatedTS: string
            }
            isMatch: boolean
        }
        matchJobFunction: {
            jobFunction: {
                jobFunctionV3Id: number
                jobFunctionV3Name: string
                groupJobFunctionV3Id: number
            }
            isMatch: boolean
        }
        matchLocation: {
            isMatch: boolean
        }
        matchSalary: {
            isMatch: boolean
        }
        countMatching: number
        careerPathTotal: number
        percentage: number
    }
    matchScore: number
    matchScoreLabel: string
    profileStrength: number
    lastUpdated: string
    isEnoughData: boolean
}

export type TResponseMatchingJob = {
    id: number
    status: number
    attributes: Partial<TMatchingJob>
}

export type TParams = {
    jobId: number
    allowExpired: string | null
    userId?: string | null
    langCode?: ELanguage | null
    allowInactive?: string
    allowIncomplete?: string
    preview?: string
}

export type TUpdateJobInvitationRequest = {
    langCode: ELanguage
    accessToken: string
    jobInvitationId?: number
    jobId: number
}
